import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Store, Save, ImagePlus, Loader2, Palette, ChevronRight, MessageCircle, Wallet, Wifi, WifiOff, Power, LogOut, RefreshCw, LayoutGrid, Rows, Volume2, Music2, Play, Upload, Trash2, MapPin, LocateFixed, ShieldCheck } from "lucide-react";
import { http, apiError } from "@/lib/api";
import { ADMIN_BASE } from "@/lib/config";
import AdminShell, { PageHead, useAdmin } from "@/components/AdminShell";
import { Toggle, Field } from "@/pages/admin/Products";

const ORDER_SOUND_OPTIONS = [
  ["default", "Default", "Nada standar aplikasi"],
  ["kitchen_bell", "Kitchen Bell", "Bel dapur yang jelas"],
  ["double_bell", "Double Bell", "Dua bunyi bel cepat"],
  ["soft_chime", "Soft Chime", "Chime lembut"],
  ["digital", "Digital", "Nada digital modern"],
  ["pop", "Pop", "Bunyi pop pendek"],
  ["classic_bell", "Classic Bell", "Bel klasik"],
  ["fast_beep", "Fast Beep", "Beep cepat"],
  ["alert_pulse", "Alert Pulse", "Pulse notifikasi"],
  ["restaurant_ding", "Restaurant Ding", "Ding restoran"],
];

function schedulePreviewTone(ctx, destination, freq, start, duration, type = "sine", strength = 0.18) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime + start);

  gain.gain.setValueAtTime(strength, ctx.currentTime + start);
  gain.gain.exponentialRampToValueAtTime(
    0.001,
    ctx.currentTime + start + duration
  );

  osc.connect(gain);
  gain.connect(destination);

  osc.start(ctx.currentTime + start);
  osc.stop(ctx.currentTime + start + duration);
}

async function previewOrderSound(config) {
  const volume = Math.max(0.05, Math.min(1, Number(config?.volume || 0.8)));

  if (config?.sound_key === "custom") {
    if (!config?.custom_data) {
      toast.error("Pilih file suara custom terlebih dahulu.");
      return;
    }

    try {
      const audio = new Audio(config.custom_data);
      audio.volume = volume;
      await audio.play();
    } catch {
      toast.error("Browser belum mengizinkan pemutaran suara.");
    }
    return;
  }

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) {
    toast.error("Browser ini tidak mendukung audio.");
    return;
  }

  const ctx = new AudioCtx();

  try {
    if (ctx.state === "suspended") {
      await ctx.resume();
    }

    const master = ctx.createGain();
    master.gain.value = volume;
    master.connect(ctx.destination);

    const key = config?.sound_key || "default";

    if (key === "kitchen_bell") {
      schedulePreviewTone(ctx, master, 1046, 0, 0.22, "sine", 0.22);
      schedulePreviewTone(ctx, master, 1568, 0.06, 0.34, "sine", 0.14);
    } else if (key === "double_bell") {
      schedulePreviewTone(ctx, master, 988, 0, 0.17, "sine", 0.20);
      schedulePreviewTone(ctx, master, 988, 0.24, 0.17, "sine", 0.20);
    } else if (key === "soft_chime") {
      schedulePreviewTone(ctx, master, 659, 0, 0.40, "sine", 0.13);
      schedulePreviewTone(ctx, master, 784, 0.10, 0.45, "sine", 0.10);
      schedulePreviewTone(ctx, master, 988, 0.20, 0.50, "sine", 0.08);
    } else if (key === "digital") {
      schedulePreviewTone(ctx, master, 880, 0, 0.10, "square", 0.10);
      schedulePreviewTone(ctx, master, 1175, 0.13, 0.10, "square", 0.10);
      schedulePreviewTone(ctx, master, 1568, 0.26, 0.14, "square", 0.09);
    } else if (key === "pop") {
      schedulePreviewTone(ctx, master, 520, 0, 0.09, "sine", 0.22);
      schedulePreviewTone(ctx, master, 760, 0.07, 0.12, "sine", 0.15);
    } else if (key === "classic_bell") {
      schedulePreviewTone(ctx, master, 784, 0, 0.45, "sine", 0.20);
      schedulePreviewTone(ctx, master, 1175, 0.02, 0.50, "sine", 0.09);
    } else if (key === "fast_beep") {
      [0, 0.12, 0.24].forEach((t) =>
        schedulePreviewTone(ctx, master, 1000, t, 0.07, "square", 0.08)
      );
    } else if (key === "alert_pulse") {
      schedulePreviewTone(ctx, master, 740, 0, 0.14, "triangle", 0.16);
      schedulePreviewTone(ctx, master, 930, 0.18, 0.14, "triangle", 0.16);
      schedulePreviewTone(ctx, master, 740, 0.36, 0.14, "triangle", 0.16);
    } else if (key === "restaurant_ding") {
      schedulePreviewTone(ctx, master, 1319, 0, 0.32, "sine", 0.18);
      schedulePreviewTone(ctx, master, 1760, 0.08, 0.40, "sine", 0.10);
    } else {
      schedulePreviewTone(ctx, master, 880, 0, 0.18, "sine", 0.18);
      schedulePreviewTone(ctx, master, 1100, 0.18, 0.24, "sine", 0.16);
    }

    setTimeout(() => {
      ctx.close().catch(() => {});
    }, 1200);
  } catch {
    ctx.close().catch(() => {});
  }
}

function SettingsInner() {
  const admin = useAdmin();
  const isOwner = admin.user?.role === "owner";
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState(null);
  const [recipient, setRecipient] = useState("");
  const poll = useRef(null);
  const [soundCfg, setSoundCfg] = useState({
    sound_key: "default",
    volume: 0.8,
    custom_name: "",
    custom_data: "",
  });
  const [soundBusy, setSoundBusy] = useState(false);
  const soundFileRef = useRef(null);

  useEffect(() => { http.get("/admin/settings").then(({ data }) => { setS(data); setRecipient(data.whatsapp?.admin_to || ""); }).catch((e) => toast.error(apiError(e))); }, []);

  useEffect(() => {
    http
      .get("/admin/my-sound-settings")
      .then(({ data }) =>
        setSoundCfg({
          sound_key: data.sound_key || "default",
          volume: Number(data.volume ?? 0.8),
          custom_name: data.custom_name || "",
          custom_data: data.custom_data || "",
        })
      )
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const tried = useRef(false);
  async function loadWa() {
    try {
      const { data } = await http.get("/admin/whatsapp/status"); setWa(data);
      if (isOwner && !tried.current && !data.connected && !data.connecting && !data.qr_data_url && !data.service_offline) {
        tried.current = true; http.post("/admin/whatsapp/connect").catch(() => {});
      }
    } catch {}
  }
  useEffect(() => { loadWa(); poll.current = setInterval(loadWa, 2500); return () => clearInterval(poll.current); }, [isOwner]);

  function onImg(key, file, max) {
    if (!isOwner) {
      toast.error(
        key === "logo_url"
          ? "Hanya pemilik yang dapat mengubah logo kedai."
          : "Hanya pemilik yang dapat mengubah gambar hero."
      );
      return;
    }

    if (!file) return;
    if (file.size > max * 1024 * 1024) return toast.error(`Maks ${max}MB`);

    const r = new FileReader();
    r.onload = () => setS((p) => ({ ...p, [key]: r.result }));
    r.readAsDataURL(file);
  }

  function chooseSound(soundKey) {
    const next = {
      ...soundCfg,
      sound_key: soundKey,
    };

    setSoundCfg(next);
    previewOrderSound(next);
  }

  function onCustomSound(file) {
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      toast.error("Pilih file audio MP3, WAV, OGG, atau format audio lain.");
      return;
    }

    if (file.size > 1024 * 1024) {
      toast.error("Ukuran suara custom maksimal 1 MB.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const next = {
        ...soundCfg,
        sound_key: "custom",
        custom_name: file.name,
        custom_data: reader.result,
      };

      setSoundCfg(next);
      previewOrderSound(next);
    };

    reader.readAsDataURL(file);
  }

  function removeCustomSound() {
    const next = {
      ...soundCfg,
      sound_key:
        soundCfg.sound_key === "custom"
          ? "default"
          : soundCfg.sound_key,
      custom_name: "",
      custom_data: "",
    };

    setSoundCfg(next);

    if (soundFileRef.current) {
      soundFileRef.current.value = "";
    }

    toast.success("Suara custom dihapus dari pilihan.");
  }

  async function saveSoundSettings() {
    setSoundBusy(true);

    try {
      const { data } = await http.patch(
        "/admin/my-sound-settings",
        soundCfg
      );

      const saved = {
        sound_key: data.sound_key || "default",
        volume: Number(data.volume ?? 0.8),
        custom_name: data.custom_name || "",
        custom_data: data.custom_data || "",
      };

      setSoundCfg(saved);
      admin.setSoundProfile?.(saved);

      toast.success("Suara notifikasi pribadi disimpan.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSoundBusy(false);
    }
  }

  function captureAttendanceLocation() {
    if (!isOwner) return;

    if (!("geolocation" in navigator)) {
      toast.error("Perangkat/browser tidak mendukung GPS.");
      return;
    }

    toast.message("Mengambil koordinat toko...");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setS((prev) => ({
          ...prev,
          attendance: {
            ...(prev.attendance || {}),
            store_latitude: Number(position.coords.latitude.toFixed(7)),
            store_longitude: Number(position.coords.longitude.toFixed(7)),
          },
        }));
        toast.success("Koordinat toko berhasil diambil.");
      },
      (error) => {
        toast.error(error?.message || "Gagal mengambil lokasi GPS.");
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  }

  async function save() {
    setBusy(true);
    try {
      const payload = {};

      if (isOwner) {
        payload.store_name = s.store_name;
        payload.app_name = s.app_name;
        payload.admin_panel_name = s.admin_panel_name;
        payload.footer_text = s.footer_text;
        payload.order_prefix = s.order_prefix;
        payload.app_icon_url = s.app_icon_url;
        payload.social_links = s.social_links || {
          facebook: "",
          instagram: "",
          whatsapp: "",
          google_maps: "",
        };
        payload.tagline = s.tagline;
        payload.address = s.address;
        payload.logo_url = s.logo_url;
        payload.hero_image = s.hero_image;
        payload.logo_shape = s.logo_shape;
        payload.menu_layout = s.menu_layout;
        payload.hero_position = s.hero_position;
        payload.cash_enabled = s.cash_enabled;
        payload.whatsapp = s.whatsapp;
        payload.attendance = s.attendance || {
          enabled: true,
          require_selfie: true,
          require_location: true,
          store_latitude: null,
          store_longitude: null,
          radius_meters: 100,
          enforce_radius: false,
          late_tolerance_minutes: 10,
          timezone: "Asia/Jakarta",
        };
      }

      const { data } = await http.patch("/admin/settings", payload);
      admin.setStore((prev) => ({ ...prev, ...data }));
      toast.success("Pengaturan disimpan");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function waAction(path, ok) { try { await http.post(`/admin/whatsapp/${path}`); toast.success(ok); loadWa(); } catch (e) { toast.error(apiError(e)); } }
  async function saveRecipient() {
    try { await http.post("/admin/whatsapp/recipient", { admin_to: recipient }); toast.success("Nomor penerima disimpan"); setS((p) => ({ ...p, whatsapp: { ...p.whatsapp, admin_to: recipient } })); }
    catch (e) { toast.error(apiError(e)); }
  }
  function setEnabled(v) {
    if (!isOwner) {
      toast.error("Hanya pemilik yang dapat mengubah Notifikasi WhatsApp.");
      return;
    }

    setS((p) => ({
      ...p,
      whatsapp: {
        ...p.whatsapp,
        enabled: v,
      },
    }));
  }

  function setCashEnabled(v) {
    if (!isOwner) {
      toast.error("Hanya pemilik yang dapat mengubah Pembayaran Cash.");
      return;
    }

    setS((p) => ({
      ...p,
      cash_enabled: v,
    }));
  }

  if (!s) return <div className="flex justify-center pt-20"><Loader2 className="wm-spin text-muted-foreground" /></div>;

  const connected = wa?.connected, connecting = wa?.connecting, offline = wa?.service_offline;
  const [hx, hy] = (s.hero_position || "50% 50%").replace(/%/g, "").split(" ").map((n) => parseInt(n) || 50);

  return (
    <>
      <PageHead eyebrow="Konfigurasi" title="Pengaturan" desc="Identitas kedai, pembayaran & notifikasi WhatsApp.">
        <button data-testid="save-settings-btn" onClick={save} disabled={busy} className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-transform active:scale-95 disabled:opacity-50">
          {busy ? <Loader2 className="wm-spin" size={16} /> : <><Save size={16} /> Simpan</>}
        </button>
      </PageHead>

      <Link to={`${ADMIN_BASE}/themes`} data-testid="go-themes" className="mb-4 flex items-center justify-between rounded-3xl border border-border bg-card p-5 transition-transform active:scale-[.99]">
        <div className="flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary"><Palette size={18} /></div><div><b className="font-heading text-base text-card-foreground">Tema & Tampilan UI</b><div className="text-xs text-muted-foreground">Pilih tema pelanggan & admin.</div></div></div>
        <ChevronRight className="text-muted-foreground" />
      </Link>

      {/* White-label / App Identity */}
      <div className="mb-4 rounded-3xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
            <Store size={18} />
          </div>
          <div>
            <b className="font-heading text-lg text-card-foreground">Identitas Aplikasi / White-label</b>
            <div className="text-xs text-muted-foreground">
              Semua nama dipisahkan agar mudah dipakai untuk brand/tenant lain.
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Nama Aplikasi / PWA">
            <input
              value={s.app_name || ""}
              readOnly={!isOwner}
              onChange={(e) => isOwner && setS({ ...s, app_name: e.target.value })}
              placeholder="Contoh: Omee Order"
              className={`input ${isOwner ? "" : "cursor-not-allowed opacity-70"}`}
            />
          </Field>

          <Field label="Nama Admin Panel">
            <input
              value={s.admin_panel_name || ""}
              readOnly={!isOwner}
              onChange={(e) => isOwner && setS({ ...s, admin_panel_name: e.target.value })}
              placeholder="Contoh: Omee Admin"
              className={`input ${isOwner ? "" : "cursor-not-allowed opacity-70"}`}
            />
          </Field>

          <Field label="Teks Footer">
            <input
              value={s.footer_text || ""}
              readOnly={!isOwner}
              onChange={(e) => isOwner && setS({ ...s, footer_text: e.target.value })}
              placeholder="Contoh: QR Table Ordering"
              className={`input ${isOwner ? "" : "cursor-not-allowed opacity-70"}`}
            />
          </Field>

          <Field label="Prefix Order ID">
            <input
              value={s.order_prefix || ""}
              readOnly={!isOwner}
              maxLength={12}
              onChange={(e) => {
                if (!isOwner) return;
                const value = e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9-]/g, "")
                  .slice(0, 12);
                setS({ ...s, order_prefix: value });
              }}
              placeholder="Contoh: OMEE"
              className={`input ${isOwner ? "" : "cursor-not-allowed opacity-70"}`}
            />
          </Field>
        </div>

        <div className="mt-4">
          <div className="mb-1 text-xs font-bold text-muted-foreground">Icon Aplikasi / PWA</div>
          <div className="flex items-center gap-3">
            <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-2xl bg-muted">
              {s.app_icon_url ? (
                <img src={s.app_icon_url} alt="App icon" className="h-full w-full object-cover" />
              ) : (
                <Store size={24} className="text-muted-foreground" />
              )}
            </div>
            <label
              className={`flex items-center justify-center gap-1.5 rounded-xl bg-muted px-3 py-2 text-xs font-bold ${isOwner ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
            >
              <ImagePlus size={14} /> Ganti Icon Aplikasi
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                disabled={!isOwner}
                onChange={(e) => onImg("app_icon_url", e.target.files?.[0], 2)}
              />
            </label>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Disarankan gambar persegi. Icon ini dipakai untuk PWA/Home Screen dan notifikasi.
          </p>
        </div>

        <div className="mt-4 rounded-2xl bg-muted/50 p-3 text-xs text-muted-foreground">
          URL admin sekarang tetap: <b className="text-card-foreground">/admin-panel</b>. URL ini bukan nama brand dan tidak berubah saat mengganti tenant.
        </div>
      </div>

      {/* Branding */}
      <div className="mb-4 rounded-3xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary"><Store size={18} /></div><b className="font-heading text-lg text-card-foreground">Identitas Kedai</b></div>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <div className={`mb-2 h-28 w-28 overflow-hidden bg-muted ${s.logo_shape === "circle" ? "rounded-full" : "rounded-2xl"}`}>{s.logo_url ? <img src={s.logo_url} alt="logo" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-muted-foreground"><Store size={26} /></div>}</div>
            <label
              className={`flex items-center justify-center gap-1.5 rounded-xl bg-muted px-3 py-2 text-xs font-bold ${isOwner ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
              onClick={(e) => {
                if (!isOwner) {
                  e.preventDefault();
                  toast.error("Hanya pemilik yang dapat mengubah logo kedai.");
                }
              }}
            >
              <ImagePlus size={14} /> Logo
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={!isOwner}
                onChange={(e) => onImg("logo_url", e.target.files?.[0], 2)}
              />
            </label>
          </div>
          <div className="space-y-3 md:col-span-2">
            <Field label="Nama Kedai">
              <input
                data-testid="store-name-input"
                value={s.store_name}
                readOnly={!isOwner}
                onClick={() => {
                  if (!isOwner) {
                    toast.error("Hanya pemilik yang dapat mengubah Nama Kedai.");
                  }
                }}
                onChange={(e) => {
                  if (!isOwner) return;
                  setS({ ...s, store_name: e.target.value });
                }}
                className={`input ${isOwner ? "" : "cursor-not-allowed opacity-70"}`}
              />
            </Field>
            <Field label="Tagline">
              <input
                value={s.tagline}
                readOnly={!isOwner}
                onClick={() => {
                  if (!isOwner) {
                    toast.error("Hanya pemilik yang dapat mengubah Tagline.");
                  }
                }}
                onChange={(e) => {
                  if (!isOwner) return;
                  setS({ ...s, tagline: e.target.value });
                }}
                className={`input ${isOwner ? "" : "cursor-not-allowed opacity-70"}`}
              />
            </Field>
            <Field label="Alamat Kedai (tampil di invoice)">
              <input
                data-testid="store-address-input"
                value={s.address || ""}
                readOnly={!isOwner}
                onClick={() => {
                  if (!isOwner) {
                    toast.error("Hanya pemilik yang dapat mengubah Alamat Kedai.");
                  }
                }}
                onChange={(e) => {
                  if (!isOwner) return;
                  setS({ ...s, address: e.target.value });
                }}
                placeholder="Jl. Contoh No. 1, Kota"
                className={`input ${isOwner ? "" : "cursor-not-allowed opacity-70"}`}
              />
            </Field>
          </div>
        </div>
        {/* Hero image */}
        <div className="mt-4">
          <div className="mb-1 text-xs font-bold text-muted-foreground">Gambar Latar (Hero) Halaman Pelanggan</div>
          <div className="h-32 overflow-hidden rounded-2xl bg-muted">{s.hero_image ? <img src={s.hero_image} alt="hero" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-muted-foreground"><ImagePlus size={26} /></div>}</div>
          <label
            className={`mt-2 flex items-center justify-center gap-1.5 rounded-xl bg-muted px-3 py-2.5 text-sm font-bold ${isOwner ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
            data-testid="hero-upload"
            onClick={(e) => {
              if (!isOwner) {
                e.preventDefault();
                toast.error("Hanya pemilik yang dapat mengubah gambar hero.");
              }
            }}
          >
            <ImagePlus size={15} /> Ganti Gambar Hero
            <input
              type="file"
              accept="image/*"
              hidden
              disabled={!isOwner}
              onChange={(e) => onImg("hero_image", e.target.files?.[0], 3)}
            />
          </label>
        </div>
      </div>

      {/* Link Sosial Media */}
      <div className="mb-4 rounded-3xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
            <MessageCircle size={18} />
          </div>

          <div>
            <b className="font-heading text-lg text-card-foreground">
              Link Sosial Media
            </b>
            <div className="text-xs text-muted-foreground">
              Hanya link yang diisi yang akan tampil sebagai icon di footer halaman pelanggan.
            </div>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Facebook">
            <input
              type="url"
              value={s.social_links?.facebook || ""}
              readOnly={!isOwner}
              onChange={(e) => {
                if (!isOwner) return;

                setS((prev) => ({
                  ...prev,
                  social_links: {
                    ...(prev.social_links || {}),
                    facebook: e.target.value,
                  },
                }));
              }}
              placeholder="https://facebook.com/namatoko"
              className={`input ${
                isOwner
                  ? ""
                  : "cursor-not-allowed opacity-70"
              }`}
            />
          </Field>

          <Field label="Instagram">
            <input
              type="url"
              value={s.social_links?.instagram || ""}
              readOnly={!isOwner}
              onChange={(e) => {
                if (!isOwner) return;

                setS((prev) => ({
                  ...prev,
                  social_links: {
                    ...(prev.social_links || {}),
                    instagram: e.target.value,
                  },
                }));
              }}
              placeholder="https://instagram.com/namatoko"
              className={`input ${
                isOwner
                  ? ""
                  : "cursor-not-allowed opacity-70"
              }`}
            />
          </Field>

          <Field label="WhatsApp">
            <input
              type="url"
              value={s.social_links?.whatsapp || ""}
              readOnly={!isOwner}
              onChange={(e) => {
                if (!isOwner) return;

                setS((prev) => ({
                  ...prev,
                  social_links: {
                    ...(prev.social_links || {}),
                    whatsapp: e.target.value,
                  },
                }));
              }}
              placeholder="https://wa.me/628123456789"
              className={`input ${
                isOwner
                  ? ""
                  : "cursor-not-allowed opacity-70"
              }`}
            />
          </Field>

          <Field label="Google Maps">
            <input
              type="url"
              value={s.social_links?.google_maps || ""}
              readOnly={!isOwner}
              onChange={(e) => {
                if (!isOwner) return;

                setS((prev) => ({
                  ...prev,
                  social_links: {
                    ...(prev.social_links || {}),
                    google_maps: e.target.value,
                  },
                }));
              }}
              placeholder="https://maps.google.com/..."
              className={`input ${
                isOwner
                  ? ""
                  : "cursor-not-allowed opacity-70"
              }`}
            />
          </Field>
        </div>

        <div className="mt-3 rounded-2xl bg-muted/50 p-3 text-xs text-muted-foreground">
          Kosongkan link jika icon tersebut tidak ingin ditampilkan di footer.
        </div>
      </div>

      {/* Tampilan menu pelanggan */}
      <div className="mb-4 rounded-3xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary"><LayoutGrid size={18} /></div><b className="font-heading text-lg text-card-foreground">Tampilan Menu Pelanggan</b></div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-bold text-muted-foreground">Bentuk Logo</div>
            <div className="flex gap-2">
              {[["square", "Kotak"], ["circle", "Bulat"]].map(([sh, l]) => (
                <button
                  key={sh}
                  data-testid={`logo-shape-${sh}`}
                  onClick={() => {
                    if (!isOwner) {
                      toast.error("Hanya pemilik yang dapat mengubah bentuk logo.");
                      return;
                    }
                    setS({ ...s, logo_shape: sh });
                  }}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl border-2 p-3 ${s.logo_shape === sh ? "border-primary" : "border-border"} ${isOwner ? "" : "cursor-not-allowed opacity-70"}`}
                >
                  <span className={`h-8 w-8 overflow-hidden bg-muted ${sh === "circle" ? "rounded-full" : "rounded-md"}`}>{s.logo_url && <img src={s.logo_url} alt="" className="h-full w-full object-cover" />}</span>
                  <span className="text-sm font-bold text-card-foreground">{l}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-bold text-muted-foreground">Tata Letak Produk</div>
            <div className="flex gap-2">
              {[["list", "1 Kolom", Rows], ["grid", "2 Kolom", LayoutGrid]].map(([v, l, Ic]) => (
                <button
                  key={v}
                  data-testid={`layout-${v}`}
                  onClick={() => {
                    if (!isOwner) {
                      toast.error("Hanya pemilik yang dapat mengubah Tata Letak Produk.");
                      return;
                    }
                    setS({ ...s, menu_layout: v });
                  }}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl border-2 p-3 text-sm font-bold text-card-foreground ${
                    s.menu_layout === v ? "border-primary" : "border-border"
                  } ${isOwner ? "" : "cursor-not-allowed opacity-70"}`}
                >
                  <Ic size={16} /> {l}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="mb-1 text-xs font-bold text-muted-foreground">Posisi Gambar Hero (geser agar pas)</div>
          <div className="relative h-32 overflow-hidden rounded-2xl bg-muted">{s.hero_image && <img src={s.hero_image} alt="hero" className="h-full w-full object-cover" style={{ objectPosition: s.hero_position }} />}</div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-muted-foreground">
              Horizontal
              <input
                type="range"
                min="0"
                max="100"
                value={hx}
                data-testid="hero-x"
                onChange={(e) => {
                  if (!isOwner) {
                    toast.error("Hanya pemilik yang dapat mengubah posisi gambar hero.");
                    return;
                  }
                  setS({
                    ...s,
                    hero_position: `${e.target.value}% ${hy}%`,
                  });
                }}
                disabled={!isOwner}
                className={`w-full accent-[hsl(var(--primary))] ${
                  isOwner ? "" : "cursor-not-allowed opacity-60"
                }`}
              />
            </label>
            <label className="text-xs font-semibold text-muted-foreground">
              Vertikal
              <input
                type="range"
                min="0"
                max="100"
                value={hy}
                data-testid="hero-y"
                onChange={(e) => {
                  if (!isOwner) {
                    toast.error("Hanya pemilik yang dapat mengubah posisi gambar hero.");
                    return;
                  }
                  setS({
                    ...s,
                    hero_position: `${hx}% ${e.target.value}%`,
                  });
                }}
                disabled={!isOwner}
                className={`w-full accent-[hsl(var(--primary))] ${
                  isOwner ? "" : "cursor-not-allowed opacity-60"
                }`}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Per-admin order notification sound */}
      <div className="mb-4 rounded-3xl border border-border bg-card p-5">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
              <Volume2 size={18} />
            </div>
            <div>
              <b className="font-heading text-lg text-card-foreground">
                Suara Notifikasi Pesanan
              </b>
              <div className="text-xs text-muted-foreground">
                Pengaturan ini khusus akun Anda. Setiap admin/karyawan dapat memakai suara yang berbeda.
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => previewOrderSound(soundCfg)}
            className="flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-xs font-bold text-card-foreground"
          >
            <Play size={14} />
            Test Suara
          </button>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {ORDER_SOUND_OPTIONS.map(([key, name, desc]) => (
            <button
              type="button"
              key={key}
              onClick={() => chooseSound(key)}
              className={`rounded-2xl border-2 p-3 text-left transition active:scale-[.98] ${
                soundCfg.sound_key === key
                  ? "border-primary bg-primary/10"
                  : "border-border bg-muted/35 hover:bg-muted/60"
              }`}
            >
              <div className="flex items-center gap-2">
                <Music2
                  size={15}
                  className={
                    soundCfg.sound_key === key
                      ? "text-primary"
                      : "text-muted-foreground"
                  }
                />
                <span className="text-sm font-bold text-card-foreground">
                  {name}
                </span>
              </div>
              <div className="mt-1 text-[11px] leading-4 text-muted-foreground">
                {desc}
              </div>
            </button>
          ))}
        </div>

        <div
          className={`mt-3 rounded-2xl border-2 p-4 ${
            soundCfg.sound_key === "custom"
              ? "border-primary bg-primary/5"
              : "border-border"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-card-foreground">
                Suara Custom
              </div>
              <div className="text-xs text-muted-foreground">
                Upload audio maksimal 1 MB. File akan tersimpan untuk akun admin ini.
              </div>
              {soundCfg.custom_name && (
                <div className="mt-1 text-xs font-semibold text-primary">
                  {soundCfg.custom_name}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <label className="flex cursor-pointer items-center gap-2 rounded-xl bg-muted px-3 py-2 text-xs font-bold text-card-foreground">
                <Upload size={14} />
                Pilih Audio
                <input
                  ref={soundFileRef}
                  type="file"
                  accept="audio/*"
                  hidden
                  onChange={(e) =>
                    onCustomSound(e.target.files?.[0])
                  }
                />
              </label>

              {soundCfg.custom_data && (
                <>
                  <button
                    type="button"
                    onClick={() => chooseSound("custom")}
                    className={`rounded-xl px-3 py-2 text-xs font-bold ${
                      soundCfg.sound_key === "custom"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-card-foreground"
                    }`}
                  >
                    Gunakan Custom
                  </button>

                  <button
                    type="button"
                    onClick={removeCustomSound}
                    className="grid h-9 w-9 place-items-center rounded-xl bg-destructive/10 text-destructive"
                    title="Hapus suara custom"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs font-bold text-muted-foreground">
            <span>Volume</span>
            <span>{Math.round(soundCfg.volume * 100)}%</span>
          </div>
          <input
            type="range"
            min="5"
            max="100"
            step="5"
            value={Math.round(soundCfg.volume * 100)}
            onChange={(e) =>
              setSoundCfg((p) => ({
                ...p,
                volume: Number(e.target.value) / 100,
              }))
            }
            onMouseUp={() => previewOrderSound(soundCfg)}
            onTouchEnd={() => previewOrderSound(soundCfg)}
            className="w-full accent-[hsl(var(--primary))]"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-muted/50 p-3">
          <div className="text-xs text-muted-foreground">
            Memilih suara akan langsung memutar preview. Tekan Simpan agar pilihan tetap dipakai setelah login ulang.
          </div>

          <button
            type="button"
            onClick={saveSoundSettings}
            disabled={soundBusy}
            className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-xs font-bold text-primary-foreground disabled:opacity-50"
          >
            {soundBusy ? (
              <Loader2 className="wm-spin" size={14} />
            ) : (
              <Save size={14} />
            )}
            Simpan Suara
          </button>
        </div>
      </div>

      {/* Employee Attendance */}
      {isOwner && (
        <div className="mb-4 rounded-3xl border border-border bg-card p-5">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
                <ShieldCheck size={18} />
              </div>
              <div>
                <b className="font-heading text-lg text-card-foreground">
                  Pengaturan Absensi Karyawan
                </b>
                <div className="text-xs text-muted-foreground">
                  Selfie, GPS, radius toko, toleransi keterlambatan, dan zona waktu.
                </div>
              </div>
            </div>

            <Toggle
              label=""
              checked={s.attendance?.enabled !== false}
              onChange={(value) =>
                setS((prev) => ({
                  ...prev,
                  attendance: {
                    ...(prev.attendance || {}),
                    enabled: value,
                  },
                }))
              }
            />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Latitude Toko">
              <input
                type="number"
                step="0.0000001"
                value={s.attendance?.store_latitude ?? ""}
                onChange={(e) =>
                  setS((prev) => ({
                    ...prev,
                    attendance: {
                      ...(prev.attendance || {}),
                      store_latitude: e.target.value,
                    },
                  }))
                }
                placeholder="-6.2000000"
                className="input"
              />
            </Field>

            <Field label="Longitude Toko">
              <input
                type="number"
                step="0.0000001"
                value={s.attendance?.store_longitude ?? ""}
                onChange={(e) =>
                  setS((prev) => ({
                    ...prev,
                    attendance: {
                      ...(prev.attendance || {}),
                      store_longitude: e.target.value,
                    },
                  }))
                }
                placeholder="106.8166667"
                className="input"
              />
            </Field>
          </div>

          <button
            type="button"
            onClick={captureAttendanceLocation}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-muted px-4 py-3 text-sm font-bold text-card-foreground"
          >
            <LocateFixed size={16} />
            Ambil Lokasi Toko Saat Ini
          </button>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <Field label="Radius Absensi (meter)">
              <input
                type="number"
                min="10"
                max="5000"
                value={s.attendance?.radius_meters ?? 100}
                onChange={(e) =>
                  setS((prev) => ({
                    ...prev,
                    attendance: {
                      ...(prev.attendance || {}),
                      radius_meters: Number(e.target.value || 100),
                    },
                  }))
                }
                className="input"
              />
            </Field>

            <Field label="Toleransi Telat (menit)">
              <input
                type="number"
                min="0"
                max="180"
                value={s.attendance?.late_tolerance_minutes ?? 10}
                onChange={(e) =>
                  setS((prev) => ({
                    ...prev,
                    attendance: {
                      ...(prev.attendance || {}),
                      late_tolerance_minutes: Number(e.target.value || 0),
                    },
                  }))
                }
                className="input"
              />
            </Field>

            <Field label="Zona Waktu">
              <select
                value={s.attendance?.timezone || "Asia/Jakarta"}
                onChange={(e) =>
                  setS((prev) => ({
                    ...prev,
                    attendance: {
                      ...(prev.attendance || {}),
                      timezone: e.target.value,
                    },
                  }))
                }
                className="input"
              >
                <option value="Asia/Jakarta">WIB — Asia/Jakarta</option>
                <option value="Asia/Makassar">WITA — Asia/Makassar</option>
                <option value="Asia/Jayapura">WIT — Asia/Jayapura</option>
              </select>
            </Field>
          </div>

          <div className="mt-4 grid gap-2 md:grid-cols-3">
            <div className="flex items-center justify-between rounded-xl bg-muted px-4 py-3">
              <div>
                <div className="text-sm font-bold text-card-foreground">Selfie wajib</div>
                <div className="text-[11px] text-muted-foreground">Kamera depan saat masuk/pulang.</div>
              </div>
              <Toggle
                label=""
                checked={s.attendance?.require_selfie !== false}
                onChange={(value) =>
                  setS((prev) => ({
                    ...prev,
                    attendance: { ...(prev.attendance || {}), require_selfie: value },
                  }))
                }
              />
            </div>

            <div className="flex items-center justify-between rounded-xl bg-muted px-4 py-3">
              <div>
                <div className="text-sm font-bold text-card-foreground">GPS wajib</div>
                <div className="text-[11px] text-muted-foreground">Koordinat + akurasi disimpan.</div>
              </div>
              <Toggle
                label=""
                checked={s.attendance?.require_location !== false}
                onChange={(value) =>
                  setS((prev) => ({
                    ...prev,
                    attendance: { ...(prev.attendance || {}), require_location: value },
                  }))
                }
              />
            </div>

            <div className="flex items-center justify-between rounded-xl bg-muted px-4 py-3">
              <div>
                <div className="text-sm font-bold text-card-foreground">Wajib dalam radius</div>
                <div className="text-[11px] text-muted-foreground">Tolak absensi jika di luar area.</div>
              </div>
              <Toggle
                label=""
                checked={Boolean(s.attendance?.enforce_radius)}
                onChange={(value) =>
                  setS((prev) => ({
                    ...prev,
                    attendance: { ...(prev.attendance || {}), enforce_radius: value },
                  }))
                }
              />
            </div>
          </div>

          <div className="mt-3 flex items-start gap-2 rounded-2xl bg-muted/50 p-3 text-xs text-muted-foreground">
            <MapPin size={15} className="mt-0.5 flex-none" />
            <span>
              Waktu absensi selalu dibuat backend. Watermark foto berisi nama karyawan,
              tanggal, jam hingga detik, koordinat GPS, akurasi, dan jarak dari toko.
              Jam kerja dan periode kerja diatur dari menu Karyawan → Kelola Shift.
            </span>
          </div>
        </div>
      )}

      {/* Payment */}
      <div className="mb-4 flex items-center justify-between rounded-3xl border border-border bg-card p-5">
        <div className="flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary"><Wallet size={18} /></div><div><b className="font-heading text-base text-card-foreground">Pembayaran Cash</b><div className="text-xs text-muted-foreground">QRIS (Midtrans) sudah aktif & dikelola otomatis oleh sistem.</div></div></div>
        <Toggle label="" checked={s.cash_enabled} onChange={setCashEnabled} />
      </div>

      {/* WhatsApp connect */}
      <div className="rounded-3xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary"><MessageCircle size={18} /></div><div><b className="font-heading text-lg text-card-foreground">Notifikasi WhatsApp</b><div className="text-xs text-muted-foreground">Notif dikirim untuk order Cash & QRIS yang LUNAS.</div></div></div>

        <div className="flex items-center justify-between rounded-xl bg-muted px-4 py-3">
          <span className="text-sm font-semibold text-card-foreground">Aktifkan Notifikasi</span>
          <Toggle label="" checked={!!s.whatsapp?.enabled} onChange={setEnabled} />
        </div>

        {/* Connection status */}
        <div className="mt-3 rounded-2xl border border-border p-4">
          <div className="flex items-center gap-2">
            {offline ? <WifiOff size={18} className="text-muted-foreground" /> : connected ? <Wifi size={18} className="text-emerald-500" /> : <WifiOff size={18} className="text-amber-500" />}
            <b className="text-sm text-card-foreground" data-testid="wa-status-text">
              {offline ? "Service WhatsApp belum aktif" : connected ? `Terhubung (${wa?.sender || "?"})` : connecting ? "Menghubungkan… scan QR di bawah" : "Belum terhubung"}
            </b>
            <button onClick={loadWa} className="ml-auto grid h-8 w-8 place-items-center rounded-lg bg-muted"><RefreshCw size={14} /></button>
          </div>

          {!connected && wa?.qr_data_url && (
            <div className="mt-3 text-center">
              <img src={wa.qr_data_url} alt="WhatsApp QR" className="mx-auto w-52 rounded-xl bg-white p-2" data-testid="wa-qr" />
              <p className="mt-2 text-xs text-muted-foreground">WhatsApp → Perangkat Tertaut → Tautkan Perangkat, lalu scan QR ini.</p>
            </div>
          )}

          {isOwner ? (
            <div className="mt-3 grid grid-cols-3 gap-2">
              <button data-testid="wa-connect" onClick={() => waAction("connect", "Menghubungkan…")} className="flex items-center justify-center gap-1.5 rounded-xl bg-primary py-2.5 text-xs font-bold text-primary-foreground"><Power size={14} /> Connect</button>
              <button onClick={() => waAction("disconnect", "Diputus")} className="flex items-center justify-center gap-1.5 rounded-xl bg-muted py-2.5 text-xs font-bold"><WifiOff size={14} /> Disconnect</button>
              <button onClick={() => waAction("logout", "Logout & reset")} className="flex items-center justify-center gap-1.5 rounded-xl bg-destructive/10 py-2.5 text-xs font-bold text-destructive"><LogOut size={14} /> Logout</button>
            </div>
          ) : <p className="mt-3 text-xs text-muted-foreground">Hanya pemilik yang dapat menyambungkan WhatsApp.</p>}
        </div>

        {/* Recipient */}
        {isOwner && (
          <div className="mt-3">
            <Field label="Nomor Admin Penerima (cth 628123456789)">
              <div className="flex gap-2">
                <input data-testid="wa-admin" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="628xxxxxxxxxx" className="input flex-1" />
                <button data-testid="wa-save-recipient" onClick={saveRecipient} className="rounded-xl bg-primary px-4 text-sm font-bold text-primary-foreground">Simpan</button>
              </div>
            </Field>
            <p className="mt-1 text-xs text-muted-foreground">Nomor pengirim = akun WhatsApp yang di-scan di atas. Penerima harus beda dari pengirim.</p>
          </div>
        )}
      </div>

      <style>{`.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--muted));border-radius:.75rem;padding:.65rem .8rem;font-size:.85rem;outline:none;color:hsl(var(--card-foreground))}`}</style>
    </>
  );
}

export default function Settings() { return <AdminShell><SettingsInner /></AdminShell>; }